import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BulkEditSectionFormService, Dropdown, type DropdownOption } from '@zag/ui';

const SECTION_ID = 'bulk-language';
type SupportedLanguage = 'en' | 'de' | 'fr' | 'it';

interface State {
  language: SupportedLanguage;
}

const LANGUAGE_KEYS: Array<{ value: SupportedLanguage; key: string }> = [
  { value: 'en', key: 'bulkEdit.users.language.english' },
  { value: 'de', key: 'bulkEdit.users.language.german' },
  { value: 'fr', key: 'bulkEdit.users.language.french' },
  { value: 'it', key: 'bulkEdit.users.language.italian' },
];

@Component({
  selector: 'sc-user-language-bulk-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Dropdown, TranslatePipe],
  template: `
    <zag-dropdown
      [value]="state().language"
      [options]="languageOptions()"
      [placeholder]="'bulkEdit.users.language.selectLanguage' | translate"
      (selectionChange)="onLanguageChange($event)"
    />
  `,
})
export class UserLanguageBulkSectionComponent implements OnInit, OnDestroy {
  private readonly formService = inject(BulkEditSectionFormService);
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly state = signal<State>({ language: 'en' });

  protected readonly languageOptions = computed<DropdownOption<SupportedLanguage>[]>(() => {
    this.currentLang();
    return LANGUAGE_KEYS.map(language => ({ value: language.value, label: this.translate.instant(language.key) }));
  });

  protected onLanguageChange(language: SupportedLanguage | null): void {
    if (language) this.state.set({ language });
  }

  ngOnInit(): void {
    this.formService.register(SECTION_ID, () => this.state());
  }

  ngOnDestroy(): void {
    this.formService.unregister(SECTION_ID);
  }
}
